import React, { useState, useEffect, useMemo } from "react";
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  PointerSensor,
  useSensor,
  useSensors,
  MeasuringStrategy,
} from "@dnd-kit/core";
import { v4 as uuidv4 } from 'uuid';
import ArrowForward from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { WhiteBlueTooltip } from "../../UIComponents";

const measuringConfig = {
  droppable: {
    strategy: MeasuringStrategy.Always,
  },
};

const DraggableItem = React.memo(({ field, mappings }) => {
  const isMapped = useMemo(() => 
    mappings.some((m) => m.jiraId === field.id),
  [mappings, field.id]);

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `jira-${field.id}`,
    data: {
      type: "jira",
      field: { id: field.id, label: field.label },
    },
    disabled: isMapped,
  });

  return (
    <div
      ref={setNodeRef}
      {...(!isMapped ? listeners : {})}
      {...(!isMapped ? attributes : {})}
      className={`jira-item 
        ${isDragging ? "dragging" : ""} 
        ${isMapped ? "mapped-disabled" : ""}`}
    >
      <span>{field.label}</span>
      {isMapped && <span className="jira-mapped-chip">{"M"}</span>}
    </div>
  );
});

const DroppableField = React.memo(({ field, mappings }) => {
  const mappedItem = useMemo(() => 
    mappings.find((m) => m.templateId === field.id),
  [mappings, field.id]);

  const { setNodeRef, isOver } = useDroppable({
    id: `template-${field.id}`,
    data: { type: "template", field },
    disabled: !!mappedItem,
  });

  return (
    <div
      ref={setNodeRef}
      className={`template-row ${isOver ? "drag-over" : ""}
      ${mappedItem ? "mapped-disabled" : ""}`}
    >
      <div className="template-label">
        {field.label}
        {field.mandatory && <span className="required">*</span>}
      </div>

      <div className="template-value">
        {mappedItem ? (
          <div className="template-mapped-chip">{"M"}</div>
        ) : (
          <span className="placeholder"></span>
        )}
      </div>
    </div>
  );
});

const MappingCanvas = React.memo(({ jiraFields, templateFields, mappingState }) => {
  const [activeDrag, setActiveDrag] = useState(null);
  const [jiraSearch, setJiraSearch] = useState("");
  const [templateSearch, setTemplateSearch] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // 5px movement required to start dragging
      },
    })
  );

  const onDragStart = (event) => {
    setActiveDrag(event.active.data.current);
  };

  const onDragEnd = (event) => {
    const { active, over } = event;
    setActiveDrag(null);

    if (!over) return;

    const dragData = active.data.current;
    const dropData = over.data.current;

    if (dragData?.type !== "jira" || dropData?.type !== "template") return;

    const jiraField = dragData.field;
    const templateField = dropData.field;

    const alreadyMapped = mappingState.mappings.some(
      (m) => m.jiraId === jiraField.id || m.templateId === templateField.id,
    );

    if (alreadyMapped) return;

    mappingState.addMapping({
      rowId: uuidv4(),
      jiraId: jiraField.id,
      jiraLabel: jiraField.label,
      templateId: templateField.id,
      templateLabel: templateField.label,
    });
  };

  const filteredJiraFields = useMemo(() => 
    jiraFields.filter((f) =>
      f.label.toLowerCase().includes(jiraSearch.toLowerCase()),
    ),
  [jiraFields, jiraSearch]);

  const filteredTemplateFields = useMemo(() => 
    templateFields.filter((f) =>
      f.label.toLowerCase().includes(templateSearch.toLowerCase()),
    ),
  [templateFields, templateSearch]);

  return (
    <DndContext 
      sensors={sensors} 
      onDragStart={onDragStart} 
      onDragEnd={onDragEnd}
      measuring={measuringConfig}
    >
      <div className="mapping-wrapper">
        {/* LEFT PANEL */}
        <div className="panel source-panel">
          <div className="panel-header blue header-with-dot">
            <span className="header-dot" /> Source - Jira Fields
          </div>

          <div className="search-wrapper">
            <SearchIcon className="search-icon" />
            <input
              className="search-box with-icon"
              placeholder="Search Jira fields..."
              value={jiraSearch}
              onChange={(e) => setJiraSearch(e.target.value)}
            />
          </div>

          <div className="panel-list">
            {filteredJiraFields.length === 0 ? (
              <div className="no-results">No fields found</div>
            ) : (
              filteredJiraFields.map((f) => (
                <DraggableItem
                  key={f.id}
                  field={f}
                  mappings={mappingState.mappings}
                />
              ))
            )}
          </div>
        </div>

        {/* MIDDLE PANEL */}
        <div className="panel destination-panel">
          <div className="panel-header green header-with-dot">
            <span className="header-dot" /> Destination - Template Fields
          </div>

          <div className="search-wrapper">
            <SearchIcon className="search-icon" />
            <input
              className="search-box with-icon"
              placeholder="Search Template fields..."
              value={templateSearch}
              onChange={(e) => setTemplateSearch(e.target.value)}
            />
          </div>

          <div className="panel-list">
            {filteredTemplateFields.length === 0 ? (
              <div className="no-results">No fields found</div>
            ) : (
              filteredTemplateFields.map((f) => (
                <DroppableField
                  key={f.id}
                  field={f}
                  mappings={mappingState.mappings}
                />
              ))
            )}
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="panel mapping-panel" >
          <div className="panel-header dark header-with-dot">
            <span className="header-dot" /> Mappings
          </div>


         { mappingState.mappings.length === 0 && <div className="mapping-drop-area">
            Drag <span className="blue-text">Jira fields</span> onto{" "}
            <span className="green-text">Template fields</span>
          </div>}

          <div className="mapped-list">
            {mappingState.mappings.map((m) => (
              <div key={m.rowId} className="mapped-row">
                <div className="mapped-center">
                  <WhiteBlueTooltip title={m.jiraLabel} placement="left" arrow>
                    <div className="mapped-pill jira-pill">{m.jiraLabel}</div>
                  </WhiteBlueTooltip>

                  <div className="mapped-arrow">
                    <ArrowForward fontSize="small" />
                  </div>
                  <WhiteBlueTooltip
                    title={m.templateLabel}
                    arrow
                    placement="right"
                  >
                    <div className="mapped-pill template-pill">
                      {m.templateLabel}
                    </div>
                  </WhiteBlueTooltip>
                </div>
                <button
                  className="remove-btn"
                  onClick={() => mappingState.removeMapping(m.rowId)}
                >
                  <CloseIcon fontSize="small" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* DRAG PREVIEW (IMPORTANT) */}
      <DragOverlay>
        {activeDrag && (
          <div className="drag-preview">{activeDrag.field.label}</div>
        )}
      </DragOverlay>
    </DndContext>
  );
});

export default MappingCanvas;
